import { Car, Gauge, Plus, Search, UserPlus, Wrench } from "lucide-react";
import { useState } from "react";

import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorBanner,
  Input,
  Modal,
  Select,
  Spinner,
} from "@/components/ui";
import CustomerFormModal from "@/components/CustomerFormModal";
import { isValidPlate, odometer, plateDigits, plateMask } from "@/lib/format";
import { useOutletContext } from "react-router-dom";

import { useFormat, useT, type Chave } from "@/lib/i18n";
import { toApiError } from "@/lib/api";
import {
  useCustomers,
  useVehicleCrud,
  useVehicleHistory,
  useVehicles,
} from "@/lib/queries";
import type { Customer, FuelType, Vehicle, VehicleType, Me } from "@/lib/types";

/** `label` é chave de tradução, não texto: constante de módulo é avaliada
 *  no import e congelaria o idioma daquele instante. */
const TIPOS: { value: VehicleType; label: Chave }[] = [
  { value: "car", label: "vtype.car" },
  { value: "suv", label: "vtype.suv" },
  { value: "van", label: "vtype.van" },
  { value: "motorcycle", label: "vtype.motorcycle" },
  { value: "truck", label: "vtype.truck" },
  { value: "other", label: "vtype.other" },
];

const COMBUSTIVEIS: { value: FuelType; label: Chave }[] = [
  { value: "", label: "fuel.none" },
  { value: "flex", label: "fuel.flex" },
  { value: "gasoline", label: "fuel.gasoline" },
  { value: "ethanol", label: "fuel.ethanol" },
  { value: "diesel", label: "fuel.diesel" },
  { value: "cng", label: "fuel.cng" },
  { value: "hybrid", label: "fuel.hybrid" },
  { value: "electric", label: "fuel.electric" },
];

const TIPO_CHAVE = Object.fromEntries(TIPOS.map((x) => [x.value, x.label])) as Record<
  string,
  Chave
>;

/** A frota que a oficina atende.
 *
 * A busca é por placa antes de qualquer coisa: no balcão o cliente diz a
 * placa, não o próprio nome. Marca, modelo e dono entram na mesma caixa
 * porque quem atende não sabe de antemão qual dos três vai lembrar.
 */
export default function Vehicles() {
  const t = useT();
  const { region } = useOutletContext<Me>().garage;
  const { locale } = useFormat();
  const [search, setSearch] = useState("");
  const [editando, setEditando] = useState<Vehicle | null>(null);
  const [criando, setCriando] = useState(false);
  const [historico, setHistorico] = useState<Vehicle | null>(null);

  // Sem trava de papel aqui, igual à ficha do cliente: a rota do backend
  // cobra `IsMember`, e quem completa o cadastro do carro é justamente a
  // recepção no check-in — esconder o botão dela só mandaria o trabalho
  // de volta para o dono.
  const { data, isLoading, isFetching } = useVehicles({ search: search || undefined });

  return (
    <div className="p-4 lg:p-8">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold lg:text-2xl">{t("nav.vehicles")}</h1>
          <p className="text-sm text-steel-muted">
            {t("vehicles.fleetCount", { n: data?.length ?? 0 })}
          </p>
        </div>
        <Button variant="primary" onClick={() => setCriando(true)}>
          <Plus size={16} />
          {t("vehicles.new")}
        </Button>
      </div>

      <div className="relative mb-4">
        <Search
          size={18}
          className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-steel-dim"
        />
        <Input
          placeholder={t("vehicles.search")}
          className="pl-10"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {isFetching && !isLoading && (
          <Spinner className="absolute top-1/2 right-3 size-4 -translate-y-1/2" />
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner className="size-8" />
        </div>
      ) : data?.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Car size={40} />}
            title={search ? t("vehicles.noneFound") : t("vehicles.noneYet")}
            description={
              search
                ? t("vehicles.tryPlate")
                : t("vehicles.emptyText")
            }
            action={!search ? <Button onClick={() => setCriando(true)}>{t("vehicles.addFirst")}</Button> : undefined}
          />
        </Card>
      ) : (
        <div className="space-y-2">
          {data?.map((v) => (
            <Card key={v.id} className="flex flex-wrap items-center gap-3 p-4">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="plate">{v.formatted_plate}</span>
                  <span className="truncate font-medium">{v.label}</span>
                  <Badge className="border-line bg-carbon-700 text-steel-muted">
                    {t(TIPO_CHAVE[v.vehicle_type])}
                  </Badge>
                  {!v.is_active && (
                    <Badge className="border-line bg-carbon-700 text-steel-dim">{t("common.inactive")}</Badge>
                  )}
                </div>
                <p className="mt-0.5 truncate text-sm text-steel-muted">
                  {v.customer_name}
                  {v.color && ` · ${v.color}`}
                  {v.odometer_km !== null && (
                    <span className="tabular"> · {odometer(v.odometer_km, region.distance_unit, locale)}</span>
                  )}
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => setHistorico(v)}>
                  <Wrench size={14} />
                  {t("vehicles.history")}
                </Button>
                <Button variant="secondary" size="sm" onClick={() => setEditando(v)}>
                  {t("vehicles.editVehicle")}
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <VehicleForm
        open={criando || editando !== null}
        vehicle={editando}
        onClose={() => {
          setCriando(false);
          setEditando(null);
        }}
      />
      <HistoryModal vehicle={historico} onClose={() => setHistorico(null)} />
    </div>
  );
}

/* ------------------------------------------------------------ formulário */
function VehicleForm({
  open,
  vehicle,
  onClose,
}: {
  open: boolean;
  vehicle: Vehicle | null;
  onClose: () => void;
}) {
  const t = useT();
  const { region } = useOutletContext<Me>().garage;
  const crud = useVehicleCrud();
  const { data: clientes } = useCustomers("");
  const [placa, setPlaca] = useState("");
  const [form, setForm] = useState<Partial<Vehicle>>({});
  const [aberto, setAberto] = useState(false);
  const [novoDono, setNovoDono] = useState(false);

  // Recarrega o formulário quando o modal abre num veículo diferente. Sem
  // isto, editar dois carros seguidos mostrava os dados do primeiro.
  if (open && !aberto) {
    setAberto(true);
    setPlaca(vehicle?.formatted_plate ?? "");
    setForm(vehicle ?? { vehicle_type: "car", is_active: true });
  }
  if (!open && aberto) setAberto(false);

  const mutation = vehicle ? crud.update : crud.create;
  const erro = mutation.isError ? toApiError(mutation.error) : null;
  const placaOk = isValidPlate(placa, region.country);

  const salvar = () => {
    const body = {
      ...form,
      plate: plateDigits(placa),
      ...(vehicle ? { id: vehicle.id } : {}),
      // `year` vazio no <input type=number> chega como "" e o DRF recusa.
      year: form.year || null,
      odometer_km: form.odometer_km || null,
    };
    mutation.mutate(body as never, { onSuccess: onClose });
  };

  return (
    <Modal open={open} onClose={onClose} title={vehicle ? t("vehicles.edit") : t("vehicles.new")} wide>
      <div className="space-y-4">
        <ErrorBanner message={erro?.message} code={erro?.code} />

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label={region.plate_label}
            value={placa}
            autoCapitalize="characters"
            spellCheck={false}
            placeholder={region.plate_example}
            error={placa && !placaOk ? "Use o formato ABC1D23 ou ABC1234." : undefined}
            onChange={(e) => setPlaca(plateMask(e.target.value, region.country))}
          />
          {/* O carro quase sempre chega antes do cadastro do dono: mandar
              a recepção sair daqui, abrir Clientes e voltar é onde o
              cadastro do veículo fica pela metade. */}
          <div>
            <Select
              label={t("vehicles.owner")}
              value={form.customer ?? ""}
              onChange={(e) => setForm({ ...form, customer: Number(e.target.value) })}
            >
              <option value="">{t("vehicles.pickCustomer")}</option>
              {clientes?.results.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
            <button
              type="button"
              onClick={() => setNovoDono(true)}
              className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-medium text-nitro-400 hover:underline"
            >
              <UserPlus size={13} />
              {t("vehicles.newCustomer")}
            </button>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label={t("vehicles.make")}
            value={form.brand ?? ""}
            placeholder={region.country === "US" ? "Honda" : "Fiat"}
            onChange={(e) => setForm({ ...form, brand: e.target.value })}
          />
          <Input
            label={t("vehicles.model")}
            value={form.model ?? ""}
            placeholder={region.country === "US" ? "Civic" : "Argo"}
            onChange={(e) => setForm({ ...form, model: e.target.value })}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <Input
            label={t("vehicles.year")}
            type="number"
            value={form.year ?? ""}
            onChange={(e) => setForm({ ...form, year: Number(e.target.value) })}
          />
          <Input
            label={t("vehicles.color")}
            value={form.color ?? ""}
            onChange={(e) => setForm({ ...form, color: e.target.value })}
          />
          {/* O dono digita na unidade DELE; o banco guarda km, sempre.
              Converter no armazenamento faria o mesmo carro atendido em
              dois países ter dois números incompatíveis na mesma ficha —
              e a ficha por placa existe justamente para não rachar. */}
          <Input
            label={`${t("vehicles.odometer")} (${region.distance_unit})`}
            type="number"
            value={
              form.odometer_km == null
                ? ""
                : region.distance_unit === "mi"
                  ? Math.round(Number(form.odometer_km) * 0.621371)
                  : form.odometer_km
            }
            hint={t("vehicles.odometerHint")}
            onChange={(e) => {
              const digitado = Number(e.target.value);
              setForm({
                ...form,
                odometer_km:
                  region.distance_unit === "mi"
                    ? Math.round(digitado / 0.621371)
                    : digitado,
              });
            }}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label={t("vehicles.type")}
            value={form.vehicle_type ?? "car"}
            onChange={(e) => setForm({ ...form, vehicle_type: e.target.value as VehicleType })}
          >
            {TIPOS.map((tipo) => (
              <option key={tipo.value} value={tipo.value}>
                {t(tipo.label)}
              </option>
            ))}
          </Select>
          <Select
            label={t("vehicles.fuel")}
            value={form.fuel ?? ""}
            onChange={(e) => setForm({ ...form, fuel: e.target.value as FuelType })}
          >
            {COMBUSTIVEIS.map((c) => (
              <option key={c.value} value={c.value}>
                {t(c.label)}
              </option>
            ))}
          </Select>
        </div>

        <Input
          label={t("common.notes")}
          value={form.notes ?? ""}
          placeholder={t("vehicles.notesPlaceholder")}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
        />

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="racing"
            disabled={!placaOk || !form.customer || mutation.isPending}
            onClick={salvar}
          >
            {mutation.isPending ? <Spinner className="size-4" /> : t("common.save")}
          </Button>
        </div>
      </div>

      {/* Já sai selecionado: quem acabou de cadastrar o dono não deveria
          precisar procurá-lo na lista logo em seguida. */}
      <CustomerFormModal
        open={novoDono}
        onClose={() => setNovoDono(false)}
        onCreated={(c: Customer) => setForm((prev) => ({ ...prev, customer: c.id }))}
      />
    </Modal>
  );
}

/* -------------------------------------------------------------- histórico */
function HistoryModal({ vehicle, onClose }: { vehicle: Vehicle | null; onClose: () => void }) {
  const t = useT();
  const { money, dayLabel } = useFormat();
  const { data, isLoading } = useVehicleHistory(vehicle?.id ?? null);

  return (
    <Modal
      open={vehicle !== null}
      onClose={onClose}
      title={vehicle ? `${vehicle.formatted_plate} · ${vehicle.label}` : ""}
      wide
    >
      {isLoading ? (
        <div className="flex justify-center py-10">
          <Spinner className="size-7" />
        </div>
      ) : data?.length === 0 ? (
        <EmptyState
          icon={<Gauge size={36} />}
          title={t("vehicles.noHistory")}
          description={t("vehicles.noHistoryText")}
        />
      ) : (
        <div className="space-y-2">
          {data?.map((a) => (
            <div
              key={a.id}
              className="flex items-center justify-between gap-3 rounded-[2px] bg-carbon-700 px-4 py-3"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">
                  {a.items.map((i) => i.service_name).join(" + ")}
                </p>
                <p className="text-xs text-steel-dim">
                  {a.professional_name} · {a.status_display}
                </p>
              </div>
              <div className="shrink-0 text-right">
                <p className="tabular text-sm">{dayLabel(a.starts_at)}</p>
                <p className="tabular text-xs text-steel-muted">{money(a.total_price)}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}
